import React, { useCallback } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';

import { LoadablePaint } from '@confluence/loadable';

import { usePreloadCreationMenuData } from '../usePreloadCreationMenuData.ts';

import { CreateContentButtonWrapper } from './CreateButtonStyles.tsx';

const CreateButton = LoadablePaint<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TopNav" */ '@atlaskit/navigation-system/top-nav-items'
			)
		).CreateButton,
});

const i18n = defineMessages({
	create: {
		id: 'create-blank-fabric-page.create.content.popover.button',
		defaultMessage: 'Create',
		description: 'The text is shown on a primary button in the global navigation bar. When clicked, it opens a popover menu allowing the user to create new content (e.g., page, blog, whiteboard).',
	},
	createContentLabel: {
		id: 'create-blank-fabric-page.create.content.aria.label',
		defaultMessage: 'Create content',
		description: 'The accessible label for the create content button in the global navigation bar, read by screen readers to describe the button purpose when no visible text is shown.',
	},
});

export const getCreateButtonTheme = (createButtonTheme) => (current, props) => {
	const { buttonStyles, spinnerStyles } = current(props);
	return {
		buttonStyles: {
			...buttonStyles,
			...createButtonTheme.default,
			margin: 0,
			fontWeight: token('font.weight.medium'),
			':hover': createButtonTheme.hover,
			':focus': createButtonTheme.focus,
			// :active doesn't work in FF, because we do a
			// e.preventDefault() on mouse down in Button.
			// '&&' is required to add more CSS specificity
			// && it not a valid CSSObject property
			// @ts-ignore
			'&&': {
				...(props.state === 'active' && createButtonTheme.active),
			},
		},
		spinnerStyles,
	};
};

export const GlobalCreateContentButton: FC<{
	triggerProps: TriggerProps;
	onClick: () => void;
}> = ({ triggerProps, onClick }) => {
	const { ref } = triggerProps;
	const preloadCreationMenuData = usePreloadCreationMenuData();

	const handleClick = useCallback(() => {
		onClick();
	}, [onClick]);

	return (
		<CreateContentButtonWrapper
			data-vc="create-content-button-wrapper"
			ref={ref}
			onMouseEnter={preloadCreationMenuData}
			onFocus={preloadCreationMenuData}
		>
			<CreateButton onClick={handleClick} testId="create-content-popover-button">
				<FormattedMessage {...i18n.create} />
			</CreateButton>
		</CreateContentButtonWrapper>
	);
};
